// THE LAST FEW THINGS OPENED FROM SEARCH (DESIGN_SYSTEM §12 row 31: the palette opens on "recent +
// suggestions", not on a blank box).
//
// It is a convenience, not state: five entity ids per account in `localStorage`, read synchronously when the
// palette opens because a spinner over five words would be worse than nothing. Every id is resolved through
// the index before it is drawn, so an entity that no longer exists simply stops appearing — a stale record
// can never produce a dead row. Storage that is blocked, full or absent is not an error state: the palette
// opens on the prompt instead.
//
// The record lives OUTSIDE the demo namespace (`sl.search.recent.<account>`), like the tree's expansion
// record, so "Reset demo" clears it by hand rather than by accident.

export const RECENT_LIMIT = 5;

export function recentKey(accountId: string): string {
  return `sl.search.recent.${accountId}`;
}

function store(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null; // private mode or blocked storage: search still works, it just forgets
  }
}

/** The ids last opened, newest first. Never throws and never returns anything but an array of strings. */
export function readRecent(accountId: string): string[] {
  try {
    const raw = store()?.getItem(recentKey(accountId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === 'string').slice(0, RECENT_LIMIT);
  } catch {
    return [];
  }
}

/** Put `id` at the front, drop a duplicate of it, keep five. Returns the new list so a caller can render it. */
export function rememberRecent(accountId: string, id: string): string[] {
  const next = [id, ...readRecent(accountId).filter((other) => other !== id)].slice(
    0,
    RECENT_LIMIT,
  );
  try {
    store()?.setItem(recentKey(accountId), JSON.stringify(next));
  } catch {
    /* quota or private mode — not worth an error */
  }
  return next;
}

/** "Reset demo" / "Start fresh": forget what was opened. */
export function clearRecent(accountId: string): void {
  try {
    store()?.removeItem(recentKey(accountId));
  } catch {
    /* as above */
  }
}
